/**
 * NewMeetingDialogView — dumb view for the "Quick Start" new-meeting modal.
 *
 * The smart wrapper at ../index.tsx owns the React Query graph (settings +
 * series), the startBrowserMeeting API call, sessionStorage handoff to
 * RecordingControls, and the post-start navigation. This view holds every
 * piece of UI state — subject, language selection, profile / mode / series,
 * the More-options disclosure, the inline error box — and emits a single
 * `onStart(payload)` callback when the user commits.
 *
 * Keyboard shortcuts (1-9 to toggle primary languages, Enter to start) live
 * here too: they're pure UI behaviour and the wrapper has no business
 * intercepting keystrokes.
 */

import { DEFAULT_TEMPLATE_FOR_PRODUCT } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Modal, Select } from "@mantine/core"
import {
  IconBolt,
  IconBrain,
  IconChevronRight,
  IconClock,
  IconEdit,
  IconMicrophone,
  IconWifiOff,
  IconX,
} from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { extractErrorMessage } from "../../../lib/api/errors"
import { formatDateTime } from "../../../lib/format-date"
import { getLabels } from "../../../lib/product-labels"
import { templateLabel } from "../../../lib/template-labels"
import type { ILanguageOption, IStartPayload, TMode } from "../interfaces"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * Full catalog of supported recording languages. The primary chips visible in
 * the dialog are derived from the company's `default_language` setting at
 * render time — see deriveSeed() below. Anything not in primaries is reachable
 * via the "+ N more" overflow.
 */
const ALL_LANGS: ILanguageOption[] = [
  { code: "nl", label: "Dutch", native: "Nederlands", flag: "🇳🇱" },
  { code: "en", label: "English", native: "English", flag: "🇬🇧" },
  { code: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "French", native: "Français", flag: "🇫🇷" },
  { code: "es", label: "Spanish", native: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Portuguese", native: "Português", flag: "🇵🇹" },
  { code: "pl", label: "Polish", native: "Polski", flag: "🇵🇱" },
  { code: "sv", label: "Swedish", native: "Svenska", flag: "🇸🇪" },
  { code: "da", label: "Danish", native: "Dansk", flag: "🇩🇰" },
  { code: "no", label: "Norwegian", native: "Norsk", flag: "🇳🇴" },
  { code: "fi", label: "Finnish", native: "Suomi", flag: "🇫🇮" },
  { code: "ja", label: "Japanese", native: "日本語", flag: "🇯🇵" },
  { code: "zh", label: "Mandarin", native: "普通话", flag: "🇨🇳" },
  { code: "ar", label: "Arabic", native: "العربية", flag: "🇸🇦" },
]

const FALLBACK_PRIMARY_CODES = ["nl", "en"] as const

interface ISettingsSeed {
  primaryCodes: string[]
  preselected: string[]
  autoDetect: boolean
}

/**
 * Derive the dialog's initial state from the company's `default_language`
 * setting. The setting is a comma-separated list of BCP-47 codes ("nl,en") or
 * the literal "multi" for auto-detect. When unset, fall back to nl+en — the
 * pragmatic default for the EU/Lexicanon market — so empty-config companies
 * don't see every supported locale on day one.
 */
function deriveSeed(raw: string | undefined): ISettingsSeed {
  if (raw === "multi") {
    return { primaryCodes: [...FALLBACK_PRIMARY_CODES], preselected: [], autoDetect: true }
  }
  const codes = (raw ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean)
    .filter((c) => ALL_LANGS.some((l) => l.code === c))
  if (codes.length > 0) {
    return { primaryCodes: codes, preselected: codes, autoDetect: false }
  }
  return {
    primaryCodes: [...FALLBACK_PRIMARY_CODES],
    preselected: [...FALLBACK_PRIMARY_CODES],
    autoDetect: false,
  }
}

export function NewMeetingDialogView({
  open,
  onClose,
  isOnline,
  product,
  defaultLanguageSetting,
  seriesList,
  isStarting,
  onStart,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()
  const labels = getLabels(i18n, product)

  const [subject, setSubject] = useState("")
  const [profile, setProfile] = useState(DEFAULT_TEMPLATE_FOR_PRODUCT[product])
  const [seriesId, setSeriesId] = useState<string | null>(null)
  const [selectedLangs, setSelectedLangs] = useState<string[]>([])
  const [autoDetect, setAutoDetect] = useState<boolean>(false)
  const [primaryCodes, setPrimaryCodes] = useState<string[]>([...FALLBACK_PRIMARY_CODES])
  const [showMore, setShowMore] = useState(false)
  const [advOpen, setAdvOpen] = useState(false)
  const [mode, setMode] = useState<TMode>("live")
  const [error, setError] = useState("")
  const [seededFromSettings, setSeededFromSettings] = useState(false)

  // Seed primaries + selection + autoDetect from the settings string the first
  // time the dialog opens with data available. The user's later edits are not
  // overwritten by re-fetches.
  useEffect(() => {
    if (!open) return
    if (seededFromSettings) return
    if (defaultLanguageSetting === undefined) return
    const seed = deriveSeed(defaultLanguageSetting)
    setPrimaryCodes(seed.primaryCodes)
    setSelectedLangs(seed.preselected)
    setAutoDetect(seed.autoDetect)
    setSeededFromSettings(true)
  }, [open, seededFromSettings, defaultLanguageSetting])

  // Reset transient state when closed so the next open starts fresh-from-settings.
  useEffect(() => {
    if (open) return
    setSubject("")
    setShowMore(false)
    setAdvOpen(false)
    setError("")
    setSeededFromSettings(false)
  }, [open])

  const isTTRPG = profile === "dnd-companion"
  const ready = autoDetect || selectedLangs.length > 0

  const primaryLangs = useMemo(
    () => primaryCodes.map((code) => ALL_LANGS.find((l) => l.code === code)).filter((l): l is ILanguageOption => !!l),
    [primaryCodes],
  )
  const overflowLangs = useMemo(() => ALL_LANGS.filter((l) => !primaryCodes.includes(l.code)), [primaryCodes])
  const visibleLangs = useMemo(
    () => (showMore ? [...primaryLangs, ...overflowLangs] : primaryLangs),
    [overflowLangs, primaryLangs, showMore],
  )

  const toggleLang = useCallback(
    (code: string): void => {
      if (autoDetect) return
      setSelectedLangs((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]))
    },
    [autoDetect],
  )

  // Keyboard shortcuts: 1-9 toggle the visible primary chips (count is dynamic
  // because primaries derive from the company setting). Esc/Enter handled below.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key >= "1" && e.key <= "9") {
        const idx = Number.parseInt(e.key, 10) - 1
        const lang = primaryLangs[idx]
        if (lang) {
          e.preventDefault()
          toggleLang(lang.code)
        }
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open, primaryLangs, toggleLang])

  const profileLabel = useMemo(() => templateLabel(i18n, profile), [i18n, profile])

  const startSubLine = useMemo(() => {
    if (autoDetect) return i18n._(msg`Auto-detect`)
    if (selectedLangs.length === 0) return i18n._(msg`Pick a language`)
    return selectedLangs.map((code) => ALL_LANGS.find((l) => l.code === code)?.label ?? code).join(" · ")
  }, [autoDetect, i18n, selectedLangs])

  const subjectPlaceholder = useMemo(() => {
    const formatted = formatDateTime(i18n, new Date(), { year: false })
    return `${i18n._(msg`Untitled meeting`)} · ${formatted}`
  }, [i18n])

  const handleStart = useCallback(async (): Promise<void> => {
    if (!ready) return
    setError("")

    // Send the user's subject if provided, else empty string. We deliberately
    // do NOT fall back to the placeholder ("Untitled meeting · {date}") — that
    // string is shown in the input as a placeholder hint, but persisting it as
    // the real subject defeats the displayMeetingTitle topic-fallback (every
    // unnamed meeting renders with the timestamp instead of its top topic).
    // The backend leaves subject empty, the frontend then renders the top
    // topic via lib/meeting-title.ts when summarisation lands.
    const trimmedSubject = subject.trim()

    const payload: IStartPayload = {
      subject: trimmedSubject,
      profile,
      seriesId,
      config: {
        languages: autoDetect ? [] : selectedLangs,
        autoDetect,
        mode,
      },
    }

    try {
      await onStart(payload)
    } catch (err) {
      // extractErrorMessage unwraps Eden Treaty `{ status, value }` envelopes so a
      // backend failure never lands in the error box as a raw "[object Object]".
      const message = extractErrorMessage(err)
      setError(message && message !== "Unknown error" ? message : i18n._(msg`Failed to start meeting`))
    }
  }, [autoDetect, i18n, mode, onStart, profile, ready, selectedLangs, seriesId, subject])

  // Enter triggers Start when ready and not focused in a non-textual control.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== "Enter") return
      if (e.target instanceof HTMLTextAreaElement) return
      if (!ready || isStarting) return
      e.preventDefault()
      handleStart().catch(() => undefined)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [handleStart, isStarting, open, ready])

  const campaignOptions = seriesList.map((s) => ({ value: s.id, label: s.name }))

  return (
    <Modal
      data-testid="new-meeting-dialog"
      opened={open}
      onClose={onClose}
      withCloseButton={false}
      centered
      padding={0}
      radius={18}
      size={560}
      overlayProps={{ backgroundOpacity: 0.55, blur: 6 }}
      transitionProps={{ transition: "pop", duration: 220 }}
      classNames={{ content: classes.modalShell, body: classes.modalBody }}
      styles={{ content: { background: "transparent", boxShadow: "none" }, body: { padding: 0 } }}
    >
      <div className={classes.modal}>
        {!isOnline && (
          <div className={classes.offline}>
            <IconWifiOff size={14} />
            <Trans>You&apos;re offline. Recording will be saved locally and uploaded when you reconnect.</Trans>
          </div>
        )}

        <div className={classes.head}>
          <div>
            <h2 className={classes.title}>
              <Trans>New {labels.meeting}</Trans>
            </h2>
            <div className={classes.sub}>
              <Trans>Pick the languages you&apos;ll speak.</Trans>
            </div>
          </div>
          <button type="button" className={classes.close} onClick={onClose} aria-label={i18n._(msg`Close dialog`)}>
            <IconX size={16} />
          </button>
        </div>

        <div className={classes.subjectField}>
          <IconEdit size={14} className={classes.subjectIcn} />
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={subjectPlaceholder}
            aria-label={i18n._(msg`Subject`)}
            data-testid="new-meeting-subject"
            // biome-ignore lint/a11y/noAutofocus: dialog focuses subject by design
            autoFocus
          />
          <span className={classes.subjectHint}>
            <Trans>Optional</Trans>
          </span>
        </div>

        <div className={classes.langHead}>
          <span className={classes.label}>
            <Trans>Languages spoken</Trans>
          </span>
          <label className={classes.auto}>
            <Trans>Auto-detect</Trans>
            <button
              type="button"
              className={classes.autoTog}
              data-on={autoDetect ? "true" : "false"}
              onClick={() => setAutoDetect((v) => !v)}
              aria-pressed={autoDetect}
              aria-label={i18n._(msg`Toggle auto-detect language`)}
            />
          </label>
        </div>

        <div className={classes.langs}>
          {visibleLangs.map((lang) => {
            const isSel = selectedLangs.includes(lang.code)
            const rank = isSel ? selectedLangs.indexOf(lang.code) + 1 : 0
            return (
              <button
                key={lang.code}
                type="button"
                className={classes.lang}
                data-selected={isSel ? "true" : "false"}
                data-dimmed={autoDetect ? "true" : "false"}
                onClick={() => toggleLang(lang.code)}
              >
                {isSel && <span className={classes.langRank}>{rank}</span>}
                <span className={classes.langFlag}>{lang.flag}</span>
                <span className={classes.langName}>
                  {lang.label}
                  <span className={classes.langNative}>{lang.native}</span>
                </span>
              </button>
            )
          })}
          {overflowLangs.length > 0 && (
            <button type="button" className={classes.langMore} onClick={() => setShowMore((v) => !v)}>
              {showMore ? i18n._(msg`Show fewer`) : i18n._(msg`${overflowLangs.length} more`)}
            </button>
          )}
        </div>

        <button
          type="button"
          className={classes.start}
          data-disabled={ready && !isStarting ? "false" : "true"}
          disabled={!ready || isStarting}
          onClick={() => handleStart().catch(() => undefined)}
          data-testid="new-meeting-start"
        >
          <span className={classes.startIcn}>
            <IconMicrophone size={20} />
          </span>
          <span className={classes.wave} aria-hidden="true">
            {Array.from({ length: 18 }, (_, i) => `wave-bar-${i}`).map((id, i) => (
              <span key={id} style={{ animationDelay: `${i * 0.07}s` }} />
            ))}
          </span>
          <span className={classes.startBody}>
            {isStarting ? (
              <Trans>Starting…</Trans>
            ) : !isOnline ? (
              <Trans>Record offline</Trans>
            ) : product === "lexicanon" ? (
              <Trans>Start meeting</Trans>
            ) : (
              labels.startAction
            )}
            <span className={classes.startSub}>
              {product === "lexicanon" ? startSubLine : `${startSubLine} · ${profileLabel}`}
            </span>
          </span>
          <span className={classes.startKbd}>⏎</span>
        </button>

        {error && <div className={classes.errorBox}>{error}</div>}

        {/* Lexicanon always records a live meeting-intelligence session, so it
            hides the advanced Profile/Mode/Campaign disclosure entirely — the
            useState defaults (meeting-intelligence + live) already drive onStart.
            Voxcanon keeps it (Meeting vs D&D, Live vs Batch, campaign link). */}
        {product !== "lexicanon" && (
          <div className={classes.advanced}>
            <div className={classes.advancedHead}>
              <button
                type="button"
                className={classes.disc}
                data-open={advOpen ? "true" : "false"}
                onClick={() => setAdvOpen((v) => !v)}
                aria-expanded={advOpen}
              >
                <span className={classes.discChevron}>
                  <IconChevronRight size={12} />
                </span>
                <Trans>More options</Trans>
              </button>
              {!advOpen && (
                <div className={classes.summary}>
                  <span className={classes.pill}>{profileLabel}</span>
                  <span className={classes.pill}>{mode === "live" ? <Trans>Live</Trans> : <Trans>Batch</Trans>}</span>
                  {isTTRPG && seriesId && (
                    <span className={classes.pill}>
                      {campaignOptions.find((c) => c.value === seriesId)?.label ?? <Trans>Campaign</Trans>}
                    </span>
                  )}
                </div>
              )}
            </div>

            {advOpen && (
              <div className={classes.advBody}>
                <div className={classes.advRow}>
                  <span className={classes.label}>
                    <Trans>Profile</Trans>
                  </span>
                  <div className={classes.seg}>
                    <button
                      type="button"
                      data-selected={profile === "meeting-intelligence" ? "true" : "false"}
                      onClick={() => {
                        setProfile("meeting-intelligence")
                        setSeriesId(null)
                      }}
                    >
                      <IconBrain size={12} />
                      <Trans>Meeting</Trans>
                    </button>
                    <button
                      type="button"
                      data-selected={profile === "dnd-companion" ? "true" : "false"}
                      onClick={() => setProfile("dnd-companion")}
                    >
                      <IconBrain size={12} />
                      <Trans>D&D</Trans>
                    </button>
                  </div>
                </div>

                <div className={classes.advRow}>
                  <span className={classes.label}>
                    <Trans>Mode</Trans>
                  </span>
                  <div className={classes.seg}>
                    <button
                      type="button"
                      data-selected={mode === "live" ? "true" : "false"}
                      onClick={() => setMode("live")}
                    >
                      <IconBolt size={12} />
                      <Trans>Live</Trans>
                    </button>
                    <button
                      type="button"
                      data-selected={mode === "batch" ? "true" : "false"}
                      onClick={() => setMode("batch")}
                    >
                      <IconClock size={12} />
                      <Trans>Batch</Trans>
                    </button>
                  </div>
                </div>

                {isTTRPG && (
                  <div className={`${classes.advRow} ${classes.advRowFull}`}>
                    <span className={classes.label}>
                      <Trans>Campaign</Trans>
                    </span>
                    <Select
                      placeholder={i18n._(msg`Optional — link to a campaign`)}
                      data={campaignOptions}
                      value={seriesId}
                      onChange={setSeriesId}
                      clearable
                      size="xs"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
